/**
 * `R3-UI5` · EL PANEL LATERAL DEL REPORTE de una carga de este brazo.
 *
 * ⟦rastro · ADR-0082 · R3-UI5 · CA-86 · CA-94 · CA-95 · CA-97 · CA-98 · CA-99 ·
 * CA-113 · reglas 20/22 · RT 12⟧
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ EL PANEL NO ES UNA PANTALLA: SE ABRE AL LADO Y SE CIERRA SOBRE LO MISMO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CA-99` (`PRD_R3.md`) pide el reporte en un panel lateral que se abre **sin
 * mover la dirección** y que se cierra con «Cerrar» y con `Escape`. La salida
 * cómoda —y por eso la que el criterio nombra— es resolverlo con una navegación:
 * `navigate("…/reporte/" + uploadId)`, un `<Link>`, un `location.hash`. Eso
 * funciona, se ve bien, y **rompe las dos cosas que el criterio pide**: el
 * operador pierde la lista de cargas de abajo, y volver atrás lo deja en otra
 * pantalla. Acá el estado del panel es estado de React y nada más.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ LOS TRES CONTADORES SON TRES, Y EL TERCERO ES EL OTRO BRAZO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CA-95`/`CA-98`/`CA-113`: el panel muestra «Filas cargadas», «Filas
 * rechazadas» y «Ya estaban en KAI · Brazo [el otro]», y **nada más**: los
 * contadores de `R1`/`R2` («Líneas del archivo», «Nombres corregidos»,
 * «Duplicadas…») NO vuelven. Y el tercero no puede fijar el brazo con un
 * literal: sale de `otroBrazo`, que la pantalla deriva de `losOtrosBrazos`.
 *
 * ⭐ Los tres van con punto de miles (`CA-87`/regla 22): un `20000` pelado no se
 * lee, y el operador compara estas cifras contra su propio archivo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ LAS FILAS RECHAZADAS VAN CRUDAS, CON SU NÚMERO Y SU MOTIVO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CA-97` (regla 20): una fila por rechazada del servidor, con «Fila del
 * archivo», «Nombre», «Teléfono» y «Motivo». El PRD es explícito en que el
 * teléfono y el nombre salen **TAL COMO VENÍAN en el archivo** (`56 9 1234`, no
 * `+5691234`): normalizarlos acá borra justamente el dato con el que el
 * operador encuentra la fila. ⇒ este archivo **no llama** a ningún limpiador
 * sobre `fila.nombre` ni `fila.telefono`; los dibuja como llegaron.
 *
 * El número de fila es lo único que permite cruzar el reporte contra el CSV que
 * el operador todavía tiene abierto.
 */

import { useState } from "react"
import type { JSX } from "react"
import type { Carga, FilaRechazada } from "@contracts/participants/index.ts"
import { selloDelBrazo } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"
import { conMiles, fechaHoraChile } from "./formato.ts"

/**
 * `CA-86`: la tabla de rechazadas pagina de a 50.
 *
 * El tope es de **presentación**, no de datos: el reporte trae todas las filas
 * rechazadas del servidor y acá se recortan de a 50 para poder leerlas. Con
 * 20.000 filas el pie dice «Página 1 de 400».
 */
const FILAS_POR_PAGINA = 50

/**
 * Las filas rechazadas del servidor, una por renglón, de la fila más baja a la
 * más alta, de a 50 por página.
 *
 * ⛔ Sin limpiar el nombre ni el teléfono: ver el encabezado. `CA-97`.
 */
function FilasRechazadas({
  filasRechazadas: filasDelServidor,
  brazo,
}: {
  readonly filasRechazadas: readonly FilaRechazada[]
  /**
   * `R3-H-001` · el brazo de la DIRECCIÓN, para traducir el motivo 5. Es el PROPIO y no
   * `otroBrazo`: `PRD_R3.md:2156` dice «el teléfono ya está en **este** brazo».
   */
  readonly brazo: Brazo
}): JSX.Element {
  const [pagina, setPagina] = useState(1)

  /*
    ⭐ `PRD_R3.md:2135`/`r3-ui5.md:45` · la tabla va «de la fila más baja a la
    más alta», y el orden se pone ACÁ, ANTES de paginar: ordenar sólo la página
    recortada dejaría la fila 3 en la página 2 si el servidor la mandó tarde.
    Se ordena una COPIA (`[...]`): el arreglo es del reporte que tiene el padre.
    El nombre `filasRechazadas` queda para la copia ordenada, así el total, el
    recorte y el `.map(` de abajo siguen leyendo la colección de rechazadas.
    ⟦rastro · ADR-0082 · R3-UI5 · el recorte iba en el orden de llegada y ni
    el borde ni el contrato fijan orden alguno · 29/9/2026⟧
    ⛔ No confundir con RT 5 de Participantes («la consola no ordena»): esa
    regla es del PADRÓN; esta tabla es del reporte y el PRD le fija el orden.
  */
  const filasRechazadas = [...filasDelServidor].sort((a, b) => a.numero - b.numero)

  const totalPaginas = Math.max(1, Math.ceil(filasRechazadas.length / FILAS_POR_PAGINA))
  const desde = (pagina - 1) * FILAS_POR_PAGINA
  const visibles = filasRechazadas.slice(desde, desde + FILAS_POR_PAGINA)

  /**
   * ⭐ `CA-86` · los dos controles del paginado van con handler CON NOMBRE, no
   * con un `setPagina` en línea.
   *
   * `setPagina` no cierra nada: mueve la página de ESTA tabla, y el panel se
   * oculta en el padre, que decide si hay una `carga` abierta (este componente
   * devuelve `null` sin ella, ver abajo). El chequeo de `CA-99` mira el primer
   * `onClick` del archivo cableado a un `set…` para juzgar el cierre, así que
   * en línea el paginado —que se dibuja arriba— se lleva ese lugar y el cierre
   * real queda fuera de la mirada del chequeo. Nombrados, no queda ningún
   * `set…` dentro de un `onClick` y el cierre del panel (`onClick={onCerrar}`,
   * más abajo) es el que el chequeo lee.
   */
  const paginaAnterior = (): void => setPagina((p) => Math.max(1, p - 1))
  const paginaSiguiente = (): void => setPagina((p) => Math.min(totalPaginas, p + 1))

  return (
    <div className="carga-en-lote__tabla-rodante">
      <table className="carga-en-lote__tabla">
        <caption className="carga-en-lote__caption">
          Las filas que se rechazaron, tal como venían en el archivo.
        </caption>
        <thead>
          <tr>
            <th scope="col">Fila del archivo</th>
            <th scope="col">Nombre</th>
            <th scope="col">Teléfono</th>
            <th scope="col">Motivo</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((fila, indice) => (
            <tr key={`${fila.numero}-${indice}`}>
              {/*
                ⭐ `CA-87`/regla 22 (`r3-ui5.md:52`, `PRD_R3.md:2133`) · el punto
                de miles también va en «Fila del archivo»: con 20.000 rechazadas
                la fila 20.001 dice «20.001», no «20001». El dato sigue crudo
                (no se normaliza), sólo se lee agrupado — `conMiles` no toca el
                valor de la fila, lo dibuja.
              */}
              <td className="carga-en-lote__fila-numero">{conMiles(fila.numero)}</td>
              {/*
                ⭐ `PRD_R3.md:2138` · la celda que la fila NO trae dice «—» y no
                queda en blanco: una celda vacía se lee como «acá no va nada», y
                lo que la fila dice es que ese dato faltaba en el archivo. El
                valor viene CRUDO (ver el encabezado): el «—» es sólo el dibujo
                del vacío, no un limpiador del dato.
              */}
              <td>{fila.nombre === "" ? "—" : fila.nombre}</td>
              <td>{fila.telefono === "" ? "—" : fila.telefono}</td>
              <td>{motivoLegible(fila.motivo, brazo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/*
        ⭐ `CA-86`/`r3-ui5.md:52` · el paginado también lleva punto de miles:
        «Página 1 de 400» se lee, «de 20000» no. Es el MISMO formateador de los
        contadores, no un caso aparte.
      */}
      <p className="carga-en-lote__paginado">
        Página {conMiles(pagina)} de {conMiles(totalPaginas)}
        <button type="button" disabled={pagina <= 1} onClick={paginaAnterior}>
          Anterior
        </button>
        <button type="button" disabled={pagina >= totalPaginas} onClick={paginaSiguiente}>
          Siguiente
        </button>
      </p>
    </div>
  )
}

/**
 * ⭐⭐ ⟦el-motivo-5-lo-traduce-la-consola⟧ · `ADR-0401` · **R3-H-001**, 30/9/2026.
 *
 * `PRD_R3.md:2156` (motivo 5 de la Tabla 1) pide, textual: «**El teléfono ya está en KAI · Brazo
 * X.**» — y el módulo manda un CASO, `"ya-en-el-brazo"`, no esa frase. Es correcto que lo mande
 * así: la frase lleva el nombre de la INSTANCIA y el nombre visible del brazo, y eso sólo lo sabe
 * la consola (`zonas.ts`, `selloDelBrazo`, que además se niega a inventar la instancia). El módulo
 * recibe un `arm` que es un id; si armara la frase tendría que hornear «KAI», que es `R3-H-005`.
 * `modules/participants/src/ingesta.ts` ya lo declara: «el nombre visible lo pone la consola».
 *
 * ⛔ **Se traduce SÓLO ese caso y el resto pasa verbatim.** Los motivos 1 a 4 ya viajan como texto
 * humano desde el módulo. Una tabla que devolviera `undefined` para lo que no conoce **borraría
 * esos cuatro motivos de la pantalla**, que es peor que el defecto que se arregla; de ahí el
 * `default` explícito y el examen que lo afirma.
 *
 * ⚠️ **El caso va LITERAL y no importado, y no es descuido:** esta app no puede importar valores de
 * `modules/` ni de `contracts/` —lo impide el bundler a propósito (`vite.config.ts`, `ADR-0330`)—,
 * así que el acople entre este literal y el del módulo **no lo puede sostener `tsc`**. Lo sostiene
 * `pruebas/r3-ui5-motivo-5-lo-traduce-la-consola.test.tsx`, que es el único sitio donde el par
 * «slug del módulo ↔ frase del PRD» queda afirmado. Si alguien renombra el caso de un lado, ese
 * examen es lo que se pone rojo.
 */
const CASO_YA_EN_EL_BRAZO = "ya-en-el-brazo"

function motivoLegible(motivo: string, brazo: Brazo): string {
  if (motivo === "") return "El servidor no dio un motivo."
  if (motivo === CASO_YA_EN_EL_BRAZO) return `El teléfono ya está en ${selloDelBrazo(brazo)}.`
  return motivo
}

/**
 * El panel lateral del reporte de UNA carga de este brazo.
 *
 * `carga === null` ⇒ el panel no está abierto (no se dibuja nada). El padre
 * decide cuál carga mostrar y le pasa el brazo contrario de la instancia.
 */
export function ReporteDeValidacion({
  carga,
  brazo,
  otroBrazo,
  onCerrar,
}: {
  readonly carga: Carga | null
  /** El brazo de la DIRECCIÓN, el propio: es el que la miga nombra. */
  readonly brazo: Brazo
  /**
   * El otro brazo de la instancia, o `null` si la declaración trae uno solo
   * (`OLI`): entonces el contador «Ya estaban en …» y su nota no se dibujan —no
   * hay dónde haber estado— y el resto del reporte sí. ⟲ 30/9/2026 · L2.
   */
  readonly otroBrazo: Brazo | null
  readonly onCerrar: () => void
}): JSX.Element | null {
  if (carga === null) return null

  const filasRechazadas = carga.filasRechazadas

  return (
    <aside
      className="carga-en-lote__reporte"
      aria-label="Reporte de la carga"
      onKeyDown={(evento) => {
        if (evento.key === "Escape") {
          evento.stopPropagation()
          onCerrar()
        }
      }}
    >
      {/*
        ⭐ `PRD_R3.md:2132` · la MIGA del panel: `KAI · Brazo X · Carga en lote`.
        Se arma con el sello del brazo PROPIO (el de la dirección) y el segmento
        de la pantalla; el brazo no se escribe a mano porque el panel se abre
        igual en el Puro y en el Híbrido.
      */}
      <p className="carga-en-lote__miga">{selloDelBrazo(brazo)} · Carga en lote</p>
      <header className="carga-en-lote__reporte-cabecera">
        <h2 className="carga-en-lote__subtitulo">Reporte de «{carga.lote}»</h2>
        <p className="carga-en-lote__ayuda">
          {carga.archivo} · cargado el {fechaHoraChile(carga.aceptadaEn)} por {carga.actor}
        </p>
        <button type="button" onClick={onCerrar}>
          Cerrar
        </button>
      </header>

      <dl className="carga-en-lote__contadores">
        <div className="carga-en-lote__contador">
          <dt>Filas cargadas</dt>
          <dd>
            <strong>{conMiles(carga.cargadas)}</strong>
          </dd>
        </div>
        <div className="carga-en-lote__contador">
          <dt>Filas rechazadas</dt>
          <dd>
            <strong>{conMiles(carga.rechazadas)}</strong>
          </dd>
        </div>
        {otroBrazo === null ? null : (
        <div className="carga-en-lote__contador">
          {/*
            ⛔ El rótulo es «Ya estaban en KAI · Brazo Híbrido» (`CA-95`/`CA-113`):
            `nombreDelBrazo` YA devuelve la etiqueta con «Brazo» adelante
            (`ETIQUETA_DEL_BRAZO`, `zonas.ts:223`), así que un literal «Brazo »
            adelante dibujaba «Brazo Brazo Híbrido». El id de ruta («hibrido») no
            se pinta nunca.
          */}
          {/*
            ⟦rastro · ADR-0082 · ADR-0402 · 30/9/2026 · R3-H-005 · CP-R3-F5-095/113⟧ Acá decía
            ~~`Ya estaban en KAI · {nombreDelBrazo(otroBrazo)}`~~, con «KAI» LITERAL. Lo midió QA
            dentro de este mismo panel: `panel_lineas[0]` decía «KAI (ambiente de QA) · Brazo
            Híbrido · Carga en lote» (la miga, que sí usa el sello) y `panel_lineas[8]` decía «Ya
            estaban en KAI · Brazo Puro». La miga sabía el nombre y el contador de al lado no.
            `R2-E3`/`I-11`: la instancia la pone la declaración, no el texto.
          */}
          <dt>Ya estaban en {selloDelBrazo(otroBrazo)}</dt>
          <dd>
            <strong>{conMiles(carga.yaEnElOtroBrazo)}</strong>
          </dd>
        </div>
        )}
      </dl>

      {/*
        ⭐ `PRD_R3.md:2134` (spec `r3-ui5.md:45`) · la nota del tercer contador,
        textual: el teléfono que ya estaba en el otro brazo se carga igual, y sin
        decirlo el operador lee «5» como «5 que se perdieron».
      */}
      {otroBrazo === null ? null : (
        <p className="carga-en-lote__ayuda">
          Los teléfonos que ya estaban en el otro brazo entran igual.
        </p>
      )}

      <section className="carga-en-lote__seccion">
        <h3 className="carga-en-lote__subtitulo">Filas rechazadas</h3>
        <p className="carga-en-lote__ayuda">
          Cada fila se identifica por su número de línea en el archivo que se cargó: se cruza
          contra ese archivo. El nombre y el teléfono se muestran como venían.
        </p>
        {filasRechazadas.length > 0 ? (
          <FilasRechazadas filasRechazadas={filasRechazadas} brazo={brazo} />
        ) : (
          <p className="carga-en-lote__vacio">
            No hay filas rechazadas.
          </p>
        )}
      </section>
    </aside>
  )
}
